import CustomLink from '@/bit/components/components.custom-link';
import useAddress from '@/hooks/useAddress';
import SearchInputController from '@/components/searchInputController';
import InputController from '@/components/inputController';
import AddressSearch from '@/components/inputControllerGMaps';
import Text, { TEXT_COLOR, TEXT_SIZE } from '@/bit/components/components.text';
import CheckboxController from '@/components/checkboxController';
import { BILLING_ADDRESS } from '@/constants/billingAddress';
import FormTemplate, { THEMES } from '@/components/formTemplate/index';
import { useWatch } from 'react-hook-form';
import Button, {
  BUTTON_TYPE,
  BUTTON_WIDTH,
  BUTTON_HEIGHT,
} from '@/bit/components/components.button';
import { useDispatch } from 'react-redux';

function Address({
  className = '',
  control,
  unregister,
  getValues,
  resetField,
  withHorizontalSeparator = false,
  formStructure = BILLING_ADDRESS,
  componentName = BILLING_ADDRESS,
  setInfo = null,
  step = null,
  customAction = null,
  boldTitle = true,
  showReturnLink = false,
  showAddressNotFoundLink = true,
  returnToDefaultAddress = null,
  setValue = null,
  info = '',
}) {
  const {
    TITLE,
    ID,
    HORIZONTAL_ADDRESS,
    VERTICAL_ADDRESS,
    ADDRESS,
    MANUAL_ADDRESS_CHECKBOX,
    FLOOR,
    DOOR,
    STREET_NUMBER,
  } = formStructure;
  const {
    getHorizontalAddresses,
    horizontalAddresses,
    horizontalAddressesLoading,
    setHorizontalAddressSelected,
    getVerticalAddresses,
    verticalAddresses,
    verticalAddressesLoading,
    setVerticalAddressSelected,
    setAutomaticAddress,
    automaticAddress,
  } = useAddress({
    formData: getValues(),
    resetField,
    componentName,
    fieldToResetName: VERTICAL_ADDRESS.ID,
    setInfo,
  });
  const dispatch = useDispatch();

  const onChangeGmaps = (data) => {
    if (data) {
      setValue(STREET_NUMBER.ID, data);
    }
  };
  return (
    <div className={`address-container ${className}`}>
      <FormTemplate
        titleText={TITLE}
        cyData={`${ID}__automatic-address`}
        theme={THEMES.LONG}
        step={step}
        withHorizontalSeparator={withHorizontalSeparator}
        boldTitle={boldTitle}
      >
        {automaticAddress && (
          <>
            <SearchInputController
              className="horizontal-address-container__input"
              searchName={HORIZONTAL_ADDRESS.NAME}
              searchId={HORIZONTAL_ADDRESS.ID}
              callBack={getHorizontalAddresses}
              options={horizontalAddresses || []}
              handleSelectOption={setHorizontalAddressSelected}
              control={control}
              rules={{
                required: HORIZONTAL_ADDRESS.RULES.REQUIRED,
                validate: (fieldValue) =>
                  HORIZONTAL_ADDRESS.RULES.VALIDATE.REQUIRED.checkFunc(
                    fieldValue
                  ) || HORIZONTAL_ADDRESS.RULES.VALIDATE.REQUIRED.message,
              }}
              placeholder={HORIZONTAL_ADDRESS.LABEL}
              searching={horizontalAddressesLoading}
            />
            {getValues(HORIZONTAL_ADDRESS.ID) &&
              getValues(HORIZONTAL_ADDRESS.ID)?.label &&
              !getValues(HORIZONTAL_ADDRESS.ID).full && (
                <SearchInputController
                  className="vertical-address-container__input"
                  searchId={VERTICAL_ADDRESS.ID}
                  searchName={VERTICAL_ADDRESS.NAME}
                  callBack={getVerticalAddresses}
                  options={verticalAddresses || []}
                  handleSelectOption={setVerticalAddressSelected}
                  control={control}
                  rules={{
                    required: VERTICAL_ADDRESS.RULES.REQUIRED,
                    validate: (fieldValue) =>
                      VERTICAL_ADDRESS.RULES.VALIDATE.REQUIRED.checkFunc(
                        fieldValue
                      ) || VERTICAL_ADDRESS.RULES.VALIDATE.REQUIRED.message,
                  }}
                  placeholder={VERTICAL_ADDRESS.LABEL}
                  searching={verticalAddressesLoading}
                />
              )}
          </>
        )}
        {!automaticAddress && (
          <>
            <AddressSearch
              control={control}
              inputId={ADDRESS.ID}
              inputName={ADDRESS.NAME}
              label={ADDRESS.LABEL}
              onChangeGmaps={onChangeGmaps}
              rules={{
                required: ADDRESS.RULES.REQUIRED,
                validate: (fieldValue) =>
                  ADDRESS.RULES.VALIDATE.REQUIRED.checkFunc(fieldValue) ||
                  ADDRESS.RULES.VALIDATE.REQUIRED.message,
              }}
            />
            <div className="address-container-vertical-wrapper">
              <InputController
                className="address-container__input"
                inputName={STREET_NUMBER.NAME}
                inputId={STREET_NUMBER.ID}
                inputMode="numeric"
                control={control}
                rules={{
                  pattern: STREET_NUMBER.RULES.PATTERN.ADDRESS_FORMAT,
                }}
                label={STREET_NUMBER.LABEL}
              />
              <InputController
                className="address-container__input"
                inputName={FLOOR.NAME}
                inputId={FLOOR.ID}
                control={control}
                rules={{
                  pattern: FLOOR.RULES.PATTERN.ADDRESS_FORMAT,
                }}
                label={FLOOR.LABEL}
              />
              <InputController
                className="address-container__input"
                inputName={DOOR.NAME}
                inputId={DOOR.ID}
                control={control}
                rules={{
                  pattern: DOOR.RULES.PATTERN.ADDRESS_FORMAT,
                }}
                label={DOOR.LABEL}
              />
            </div>
          </>
        )}
        {showReturnLink && (
          <CustomLink
            className="address-container__return-link"
            role="button"
            onClick={() => {
              unregister(HORIZONTAL_ADDRESS.ID);
              unregister(VERTICAL_ADDRESS.ID);
              returnToDefaultAddress();
            }}
            taggingInfo={{
              componentVariation: 'all',
              componentContent: 'Volver a dirección inicial',
              componentProduct: '',
              componentName: 'link',
            }}
          >
            <Text
              text="Volver a dirección inicial"
              color={TEXT_COLOR.BLUEA}
              size={TEXT_SIZE.CAPTION2}
            />
          </CustomLink>
        )}
        {automaticAddress && showAddressNotFoundLink && (
          <CustomLink
            className="address-container__not-found-link"
            role="button"
            onClick={() => {
              const formData = getValues();
              dispatch(
                setInfo({
                  formData,
                  skipCheckCoverage: true,
                  isManualAddress: true,
                })
              );
              unregister(HORIZONTAL_ADDRESS.ID);
              unregister(VERTICAL_ADDRESS.ID);
              setAutomaticAddress(false);
            }}
            taggingInfo={{
              componentVariation: 'all',
              componentContent: MANUAL_ADDRESS_CHECKBOX.LABEL,
              componentProduct: '',
              componentName: 'link',
            }}
          >
            <Text
              text={MANUAL_ADDRESS_CHECKBOX.LABEL}
              color={TEXT_COLOR.BLUEA}
              size={TEXT_SIZE.CAPTION2}
            />
          </CustomLink>
        )}
        {customAction && (
          <Button
            onClick={customAction}
            label="Siguiente paso"
            className="address-container__actions--save-button"
            type={BUTTON_TYPE.PRIMARY}
            width={BUTTON_WIDTH.DEFAULT}
            height={BUTTON_HEIGHT.SMALL}
            taggingInfo={{
              componentName: 'button',
              componentVariation: 'all',
              componentContent: 'Siguiente paso',
              componentPerso: '',
              componentProduct: '',
            }}
          />
        )}
        {info && (
          <div className="address-container__text">
            <Text
              text={info}
              color={TEXT_COLOR.GREY5}
              size={TEXT_SIZE.CAPTION2}
            />
          </div>
        )}
      </FormTemplate>
      <style jsx>
        {`
          .address-container {
            margin-bottom: 12px;
            :global(.address-container__select) {
              margin: 0px 0 0px 0px;
            }
            :global(.address-container__input) {
              margin: 8px 0 0px 0px;
            }

            :global(.horizontal-address-container__input) {
              margin: 0px 0 0px 0px;
            }
            :global(.vertical-address-container__input) {
              margin: 8px 0px 0px 0px;
            }
            :global(.no-address__link) {
              margin: 5px 0px 0px 0px;
            }
            :global(.address-container__checkbox) {
              width: fit-content;
              margin: 8px 0 0 14px;
            }
            :global(.address-container__return-link) {
              width: fit-content;
              margin: 8px 0 0 14px;
            }
            :global(.address-container__not-found-link) {
              width: fit-content;
              margin: 8px 0 0 14px;
              display: block;
            }
            :global(.address-container__actions--save-button) {
              margin: 12px 0 0 0;
            }
            .address-container-vertical-wrapper {
              display: flex;
              justify-content: space-between;
              gap: 8px;
            }
            .address-container__text {
              margin-top: 8px;
            }
          }
        `}
      </style>
    </div>
  );
}

export default Address;
